// components/EmployeesMultiSelectSearchbar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/24/solid";

const MIN_VISIBLE   = 6;  // soglia "riempi fino a..."
const MAX_TOPUPS    = 5;  // pagine extra max per riempire
const SCROLL_MARGIN = 12; // px dal fondo per triggerare lo scroll

export default function EmployeesMultiSelectSearchbar({
  token,
  selectedEmployees = [],
  onChange,
  getEmployees,                // async (token, q, filterBy, ou, page, pageSize, permission, start, end, sortCfg) -> { results, next? }
  pageSize = 10,
  placeholder = "Seleziona dipendenti...",
  employeeRolePairs = [],
  ou = null,
  permission = null,
  filterBy,                    // default "hr_active"
  contractStartDate = null,
  contractEndDate = null,
  sortConfig = null,
  className = "",
  inputClassName = "",
  positionOptions,
  openDirection = "bottom",
  compactSelection = false, // Solo lista dipendenti: nome nella barra, X e nessun chip
}) {
  const effectiveFilterBy = filterBy || "hr_active";

  // UI state
  const [query, setQuery]         = useState("");
  const [activated, setActivated] = useState(false);
  const [page, setPage]           = useState(1);
  const [hasMore, setHasMore]     = useState(true);
  const [loading, setLoading]     = useState(false);
  const [options, setOptions]     = useState([]); // [{ id, label, original }]
  const [noResults, setNoResults] = useState(false);

  const inputRef  = useRef(null);
  const buttonRef = useRef(null);
  const reqIdRef  = useRef(0);
  const openRef   = useRef(false);
  const endReachedRef = useRef(false);

  // Esclusioni (selezionati e rolePairs)
  const excludedKey = useMemo(() => {
    const ids = [
      ...(employeeRolePairs?.map(p => p.employeeId) ?? []),
      ...(!compactSelection ? (selectedEmployees?.map(e => e.id) ?? []) : []),
    ].map(String);
    ids.sort((a,b)=>a.localeCompare(b));
    return ids.join(",");
  }, [employeeRolePairs, selectedEmployees, compactSelection]);

  const excludeSet = useMemo(
    () => new Set(excludedKey ? excludedKey.split(",").filter(Boolean) : []),
    [excludedKey]
  );

  const labelOf  = (e) => (e?.last_name && e?.first_name) ? `${e.last_name} ${e.first_name}` : (e?.cf ?? "—");
  const toOption = (e) => ({ id: e.id, label: labelOf(e), original: e });
  const selectedCompactEmployee = compactSelection ? selectedEmployees[0] : null;

  // Dedup selezioni in ingresso
  const selectedOptions = useMemo(() => {
    const map = new Map((selectedEmployees ?? []).map(e => [String(e.id), e]));
    return Array.from(map.values()).map(toOption);
  }, [selectedEmployees]);

  const resetList = () => {
    setOptions([]);
    setPage(1);
    setHasMore(true);
    setNoResults(false);
    endReachedRef.current = false;
  };

  // Core fetch con top-up fino a MIN_VISIBLE
  const fetchPage = async ({ reset = false, open = false, force = false, searchQuery = query } = {}) => {
    if (!getEmployees || (!activated && !force) || (!open && !force)) return;
    if ((noResults && !reset) || (!hasMore && !reset) || (endReachedRef.current && !reset)) return;
    if (loading && !force) return;

    setLoading(true);
    const myReq = ++reqIdRef.current;

    try {
      let nextPage   = reset ? 1 : page;
      let aggregated = reset ? [] : options;
      let loops      = 0;
      let lastHasNext = true;

      while (true) {
        const res = await getEmployees(
          token,
          (searchQuery && searchQuery.trim()) ? searchQuery : null,
          effectiveFilterBy,
          ou,
          nextPage,
          pageSize,
          permission,
          contractStartDate,
          contractEndDate,
          sortConfig
        );

        if (myReq !== reqIdRef.current) return; // risposta superata

        const arr = Array.isArray(res?.results) ? res.results : [];

        // calcolo "hasNext" SOLO da `next` se l'API lo espone
        const hasNextViaLink = Object.prototype.hasOwnProperty.call(res ?? {}, "next")
          ? Boolean(res.next)
          : null;

        if (nextPage === 1 && arr.length === 0) {
          // Nessun risultato alla prima pagina
          setOptions([]);
          setHasMore(false);
          setNoResults(true);
          setPage(1);
          endReachedRef.current = true;
          break;
        }

        const mapped = arr.filter(e => !excludeSet.has(String(e.id))).map(toOption);
        if (mapped.length) {
          const dedupMap = new Map([...aggregated, ...mapped].map(o => [String(o.id), o]));
          aggregated = Array.from(dedupMap.values());
        }


        // Avanza pagina solo se sappiamo che esiste una successiva
        if (hasNextViaLink === true) {
          nextPage  += 1;
          lastHasNext = true;
        } else if (hasNextViaLink === false) {
          lastHasNext = false;
        } else {
          // `next` non esiste: fermiamoci in modo prudente (evita overshoot → 404)
          lastHasNext = false;
        }

        // Uscite: abbiamo riempito, non c'è next, o abbiamo fatto abbastanza top-up
        if (aggregated.length >= MIN_VISIBLE) break;

        if (lastHasNext === false) break;
        loops += 1;
        if (loops >= MAX_TOPUPS) break;
      }

      if (aggregated.length === 0) {
        // Nessun elemento visibile anche dopo i top-up
        setOptions([]);
        setHasMore(false);
        setNoResults(true);
        setPage(1);
        endReachedRef.current = true;
      } else {
        aggregated.sort((a,b)=>a.label.localeCompare(b.label, "it", {sensitivity:"base"}));
        setOptions(aggregated);
        setHasMore(lastHasNext === true);
        // se sappiamo che esiste una prossima pagina, `nextPage` è già stato incrementato;
        // altrimenti NON incrementare per non chiedere una pagina fuori range.
        setPage((p) => nextPage);
        if (lastHasNext === false) endReachedRef.current = true;
      }
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
        if (err?.response?.status === 404) {
          // pagina fuori range = fine lista (non loggare, non proseguire)
          endReachedRef.current = true;
          setHasMore(false);
          return;
        }
        console.error("Errore fetch dipendenti:", err);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  };

  // Reset quando cambiano i filtri esterni
  useEffect(() => {
    resetList();
    setActivated(false);
    setQuery("");
    reqIdRef.current += 1;
  }, [ou, effectiveFilterBy, permission, contractStartDate, contractEndDate, sortConfig, pageSize]);

  // Se dopo una selezione (quindi excludeSet cresce) le visibili scendono sotto MIN_VISIBLE e la tendina è aperta, fai top-up
  useEffect(() => {
    if (!activated || !openRef.current || loading || noResults) return;
    if (options.length < MIN_VISIBLE && hasMore) {
      fetchPage({ open: true }); // niente reset: continuiamo da dove siamo
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedKey]); // reagisci al cambiamento delle esclusioni

  // Apertura cliccando ovunque sulla barra, ma NON su icona/chip/reset
  const onBarMouseDown = (open) => (e) => {
    if (e.target.closest("[data-ignore-open]") || e.target.closest("[data-open-trigger]")) return;
    e.preventDefault();
    setActivated(true);
    setNoResults(false);
    inputRef.current?.focus();
    if (!open) {
      requestAnimationFrame(() => buttonRef.current?.click?.());
    }
  };

  // Click sull’icona: apri e avvia fetch se serve
  const onButtonClick = () => {
    setActivated(true);
    if (options.length === 0 && !loading && !noResults) {
      resetList();
      reqIdRef.current += 1;
      fetchPage({ reset: true, force: true });
    }
  };

  // Focus input: kickoff prima pagina se serve
  const handleFocus = () => {
    setActivated(true);
    if (options.length === 0 && !loading && !noResults) {
      resetList();
      reqIdRef.current += 1;
      fetchPage({ reset: true, force: true });
    }
  };

  // Ricerca ad ogni carattere
  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setActivated(true);
    setNoResults(false);

    resetList();
    reqIdRef.current += 1;
    fetchPage({ reset: true, force: true, searchQuery: val });
  };

  // Scroll infinito
  const handleScroll = (open) => (e) => {
    if (!open || !activated || !hasMore || loading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_MARGIN) {
      fetchPage({ open: true });
    }
  };

  // Selezione: dedup, chiudi pannello, poi pulisci
  const handleSelectAndClose = (opts) => {
    const next = (opts ?? []).map(o => o.original);
    const dedup = Array.from(new Map(next.map(e => [String(e.id), e])).values());
    onChange?.(compactSelection ? dedup.slice(-1) : dedup);

    inputRef.current?.blur();
    if (openRef.current) buttonRef.current?.click?.();

    requestAnimationFrame(() => {
      setQuery("");
      // NON metto noResults=true qui: potrebbero esserci altre pagine da caricare
      setOptions(prev => prev.filter(o => !dedup.some(e => String(e.id) === String(o.id))));
      // Mantieni page/hasMore: il top-up post-selezione (useEffect su excludedKey) penserà a riempire
    });
  };

  // Reset compatto: pulisce la selezione senza riaprire la tendina.
  const resetCompactSelection = (e) => {
    e.preventDefault();
    e.stopPropagation();
    reqIdRef.current += 1;
    onChange?.([]);
    setQuery("");
    resetList();
    setActivated(false);
    inputRef.current?.blur();
  };

  const removeChip = (id) => {
    const next = selectedEmployees.filter(e => e.id !== id);
    onChange?.(next);
  };

  return (
    <div className={`w-full ${className}`}>
      <Combobox by="id" value={selectedOptions} onChange={handleSelectAndClose} multiple>
        {({ open }) => {
          openRef.current = open;

          return (
            <div className="relative">
              <div
                className="group relative rounded-md border border-gray-200 bg-white shadow-sm"
                onMouseDown={onBarMouseDown(open)}
              >
                <div className={`relative flex items-center px-3 ${compactSelection ? "h-10 py-0" : "py-1"}`}>
                  <Combobox.Input
                    ref={inputRef}
                    value={query}
                    onFocus={handleFocus}
                    onChange={handleInputChange}
                    placeholder={selectedCompactEmployee ? labelOf(selectedCompactEmployee) : placeholder}
                    className={`w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-sm text-gray-700 ${selectedCompactEmployee ? "placeholder:text-indigo-700 placeholder:font-medium" : "placeholder:text-gray-400"} ${inputClassName}`}
                    displayValue={() => query}
                  />
                  {selectedCompactEmployee && (
                    <button
                      type="button"
                      data-ignore-open
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={resetCompactSelection}
                      className="ml-2 inline-flex items-center rounded-md p-1.5 text-gray-400 hover:text-red-600 focus:outline-none"
                      aria-label="Reset ricerca dipendente"
                      title="Reset ricerca"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                  <Combobox.Button
                    ref={buttonRef}
                    data-open-trigger
                    onClick={onButtonClick}
                    className="ml-2 inline-flex items-center rounded-md p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <ChevronUpDownIcon className="h-5 w-5" />
                  </Combobox.Button>
                </div>

                {!compactSelection && selectedEmployees.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 py-2 px-2 border-t border-gray-100">
                    {selectedEmployees.map((emp) => (
                      <span
                        key={emp.id}
                        className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-800"
                      >
                        {labelOf(emp)}
                        <button
                          type="button"
                          data-ignore-open
                          className="rounded p-0.5 text-indigo-600 hover:bg-indigo-200 focus:outline-none"
                          onClick={(e) => { e.stopPropagation(); removeChip(emp.id); }}
                          aria-label="Rimuovi"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      data-ignore-open
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange?.([]);
                        setQuery("");
                        resetList();
                        reqIdRef.current += 1;
                        setActivated(false);
                        inputRef.current?.focus();
                      }}
                      className="ml-1 inline-flex items-center rounded-lg bg-indigo-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-indigo-500 focus:outline-none"
                      aria-label="Reset selezione"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>

              {/* Kickoff all’apertura (se serve) */}
              {open && activated && options.length < MIN_VISIBLE && !loading && !noResults && (
                <AutoKickoff run={() => fetchPage({ open: true })} />
              )}

              <Transition
                as="div"
                show={open}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options
                  onScroll={handleScroll(open)}
                  className={`${positionOptions ? positionOptions : "absolute"} ${
                    openDirection === "top" ? "bottom-full mb-1" : "mt-1"
                  } z-30 mt-1 max-h-64 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-md ring-1 ring-black/5 focus:outline-none border border-gray-300`}
                >
                  {!loading && options.length === 0 && (
                    <div className="px-3 py-2 text-gray-500">
                      {query ? "Nessun risultato" : "Nessun dipendente da selezionare"}
                    </div>
                  )}

                  {options.map((opt) => {
                    const selected   = selectedEmployees.some(e => e.id === opt.id);
                    const isInactive = opt?.original?.app_activate === false;
                    return (
                      <Combobox.Option
                        key={opt.id}
                        value={opt}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-9 pr-3 ${
                            active ? (isInactive ? "bg-gray-200 text-gray-600" : "bg-indigo-50 text-gray-700")
                                   : (isInactive ? "bg-gray-100 text-gray-500" : "bg-white text-gray-700")
                          }`
                        }
                      >
                        <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                          <span className="flex justify-between">
                            {opt.label}
                            {isInactive && (
                              <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium text-gray-600 bg-gray-300">
                                Non attivo
                              </span>
                            )}
                          </span>
                        </span>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-indigo-600">
                            <CheckIcon className="h-5 w-5" />
                          </span>
                        )}
                      </Combobox.Option>
                    );
                  })}

                  {loading && <div className="px-3 py-2 text-gray-500">Caricamento…</div>}
                </Combobox.Options>
              </Transition>
            </div>
          );
        }}
      </Combobox>
    </div>
  );
}

/** Avvia una fetch una sola volta quando montato e la condizione è vera */
function AutoKickoff({ run }) {
  useEffect(() => { run?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}